
import BaseListPresenter from "../../../base/BaseListPresenter";
import {
  hasLength,
  createEmptyFilters,
  _parseDateRange,
  _getSetupsHolder,
  filterBySuspensionDate,
} from "./helper-payroll/HelperFunction";
import { QUERY_KEYS } from "./helper-payroll/constants/payrollConstants";
import { buildFilterClause, buildFilters } from "./helper-payroll/payrollFilterHelpers";
import { getSelectFilterPlan } from "./helper-payroll/payrollFilterPlans";
import { getMonthIndex } from "./other-functions/OtherFunctions";

class PayrollSetupsPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
    this.query = {};
  }

  init() {
    this.limit = 50;
    this.where = {};
    this.search = {};
    this.filter = createEmptyFilters();
    this.include = ["all"];
    this.keys = QUERY_KEYS;
    this.sort = { Firstname: 1, Middlename: 1, Surname: 1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const filterWhere = Object.values(this.filter || {}).reduce((acc, filterValue) => {
      if (!filterValue) return acc;
      return { ...acc, ...filterValue };
    }, {});

    const query = {
      limit: this.limit,
      skip: skip,
      where: {
        ...this.where,
        ...this.search,
        ...filterWhere,
      },
      include: this.include,
    };

    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();

    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = objects;
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  onSelect(index) {
    const selected = this.objects[index];
    const prevSelected = this.view.state.selected || [];
    const i = prevSelected.findIndex((item) => item.id === selected.id);

    let newSelected;
    if (i > -1) {
      newSelected = [...prevSelected.slice(0, i), ...prevSelected.slice(i + 1)];
    } else {
      newSelected = [...prevSelected, selected];
    }

    this.view.setState({ selected: newSelected });
  }

  patchPanelState(partialOrFn) {
    if (!partialOrFn) return;
    if (typeof partialOrFn === "function") {
      this.view.setState((prevState) => ({ panelState: partialOrFn(prevState.panelState || {}) }));
      return;
    }

    this.view.setState((prevState) => ({ panelState: { ...prevState.panelState, ...partialOrFn }}));
  }

  onChangeFilter(type, value, field) {
    const where = buildFilterClause(type, value, field, this.filter);
    this.applyFilter(where, field);
  }

  setFilters(nextFilters = {}, preserveKeys = []) {
    this.filter = buildFilters(this.filter, nextFilters, preserveKeys);
    this.query = this.createQuery();
    this.reset();
    this.getObjects();
  }

  applyFilter(where, filterKey) {
    this.reset();
    if (filterKey) {
      this.filter[filterKey] = where;
    }
    this.query = this.createQuery();
    this.getObjects();
  }

  async handleSelectChange(field, value, emptyFields) {
    let setups = null;
    if (field === "payItems") {
      const { payrollTypes, employmentType } = this.view.state.panelState;
      setups = await _getSetupsHolder({
        payrollTypes,
        employmentType,
        payItems: { value },
      });
    }

    const payrollType = this.view.state?.panelState?.payrollTypes?.value;
    const plan = getSelectFilterPlan(field, value, setups, this.filter, payrollType);
    if (plan) {
      this.setFilters(plan.nextFilters ?? {}, plan.preserveKeys ?? []);
    }

    this.patchPanelState((prevPanel) => {
      const prevValue = prevPanel?.[field]?.value;
      let next = { ...prevPanel, [field]: { value }, query: this.query };

      if (!hasLength(value) || value.id !== prevValue?.id) {
        next = { ...next, ...emptyFields, setups };
      }

      return next;
    });
  }

  onChangeDate(dateString, objects) {
    const datePicked = _parseDateRange(dateString);
    const monthIndex = getMonthIndex(datePicked);
    const filterEmployees = filterBySuspensionDate(objects, datePicked);

    this.patchPanelState({ datePicked, filterEmployees, monthIndex });
  }

  async getObjects() {
    await this.countObjects();
    await this.findObjects();
  }

  onFileUpload = (file) => {
    this.patchPanelState({ csvData: file });
  };

  onToggleModal(toggle = null, object = null) {
    const isGenerate = toggle === "generate";
    this.patchPanelState({
      isModalContent: toggle === "open",
      ...(isGenerate || toggle === "close" ? { isFloatingForm: isGenerate } : {}),
      ...(isGenerate && object ? { payrollPeriod: object } : {}),
    });
  }
}

export default PayrollSetupsPresenter;
